import Decimal from 'decimal.js';
import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string or
// non-negative number on the wire (matches the convention in lva.ts / lva-count.ts).
// Used for the non-negative amount columns (registerCost / glDebit / totals).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// variance = registerCost − glDebit, which is SIGNED (GL debit can exceed the
// register cost). MoneySchema is non-negative-only, so variance needs a signed
// decimal: any finite decimal string OR any finite number.
const SignedDecimalLike = z.union([
    z.string().refine((val) => {
        try {
            return new Decimal(val).isFinite();
        }
        catch {
            return false;
        }
    }, { message: 'Invalid decimal value: must be a finite decimal string' }),
    z.coerce.number().finite(),
]);
// ============================================================
// Memorandum-vs-GL tie-out (Phase D — the new control)
// Proves every off-balance LVA register row has a matching GL expense posting.
// Per-row status: MISSING_JV (no linked journalEntryId) / DRIFT (variance ≠ 0) /
// OK (registerCost == Σ of the linked JV's LVA-expense debit line(s)).
// ============================================================
export const LvaGlTieoutStatusSchema = z.enum(['OK', 'DRIFT', 'MISSING_JV']);
// One detail row per in-scope LvaItem. branch/department are nullable display
// scope; glDebit = Σ debit on the linked JV's LVA-expense line(s); variance =
// registerCost − glDebit. period is the acquiredDate→YYYYMM grouping key.
export const LvaGlTieoutRowSchema = z.object({
    period: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    branchName: z.string().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    lvaItemId: z.uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    acquiredDate: z.coerce.date(),
    registerCost: DecimalLike,
    journalEntryId: z.uuid().nullable().optional(),
    glDebit: DecimalLike,
    variance: SignedDecimalLike,
    status: LvaGlTieoutStatusSchema,
});
// Grouped totals across the returned rows. counts segment the rows by status.
export const LvaGlTieoutSummarySchema = z.object({
    rowCount: z.number().int().nonnegative(),
    totalRegisterCost: DecimalLike,
    totalGlDebit: DecimalLike,
    totalVariance: SignedDecimalLike,
    okCount: z.number().int().nonnegative(),
    driftCount: z.number().int().nonnegative(),
    missingJvCount: z.number().int().nonnegative(),
});
// GET companies/:companyId/lva-items/gl-tieout response.
export const LvaGlTieoutResponseSchema = z.object({
    rows: z.array(LvaGlTieoutRowSchema),
    summary: LvaGlTieoutSummarySchema,
});
//# sourceMappingURL=lva-report.js.map